import { useState, useEffect } from "react"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "./useAuth"
import { toast } from "sonner"

export interface CooperativeMembership {
  id: string
  user_id: string
  application_date: string
  status: string
  personal_data: any
  shares_subscribed: number
  membership_fee: number
  digital_signature_requested_at?: string
  digital_signature_completed_at?: string
  payment_status: string
  approved_at?: string
  approved_by?: string
  rejection_reason?: string
  created_at: string
  updated_at: string
}

export interface MembershipApplicationData {
  firstName: string
  lastName: string
  birthDate: string
  address: string
  city: string
  postalCode: string
  phone: string
  email: string
  profession: string
  income: string
  sharesSubscribed: number
  agreeToStatutes: boolean
  agreeToDataProcessing: boolean
}

export const useCooperativeMembership = () => {
  const { user } = useAuth()
  const [membership, setMembership] = useState<CooperativeMembership | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchMembership = async () => {
    if (!user) {
      setLoading(false)
      return null
    }

    try {
      const { data, error } = await supabase
        .from('cooperative_memberships')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle()

      if (error) throw error
      setMembership(data)
      return data
    } catch (error) {
      console.error('Error fetching membership:', error)
      toast.error('Fehler beim Laden der Mitgliedschaft')
      return null
    } finally {
      setLoading(false)
    }
  }

  const submitApplication = async (applicationData: MembershipApplicationData): Promise<{ success: boolean, data?: CooperativeMembership }> => {
    if (!user) {
      toast.error('Sie müssen eingeloggt sein')
      return { success: false }
    }

    try {
      const { data, error } = await supabase
        .from('cooperative_memberships')
        .insert({
          user_id: user.id,
          personal_data: {
            firstName: applicationData.firstName,
            lastName: applicationData.lastName,
            birthDate: applicationData.birthDate,
            address: applicationData.address,
            city: applicationData.city,
            postalCode: applicationData.postalCode,
            phone: applicationData.phone,
            email: applicationData.email,
            profession: applicationData.profession,
            income: applicationData.income
          },
          shares_subscribed: applicationData.sharesSubscribed,
          membership_fee: applicationData.sharesSubscribed * 100, // 100€ per share
          status: 'pending',
          payment_status: 'pending'
        })
        .select()
        .single()

      if (error) throw error

      setMembership(data)
      toast.success('Antrag erfolgreich eingereicht!')
      
      // Send notification emails to admins and applicant
      await sendApplicationNotification(data.id, applicationData)
      
      return { success: true, data }
    } catch (error) {
      console.error('Error submitting application:', error)
      toast.error('Fehler beim Einreichen des Antrags')
      return { success: false }
    }
  }

  const sendApplicationNotification = async (membershipId: string, applicationData: MembershipApplicationData): Promise<boolean> => {
    try {
      const { error } = await supabase.functions.invoke('send-cooperative-application', {
        body: {
          membershipId,
          personalData: {
            firstName: applicationData.firstName,
            lastName: applicationData.lastName,
            email: applicationData.email,
            phone: applicationData.phone,
            address: applicationData.address,
            city: applicationData.city,
            postalCode: applicationData.postalCode,
            profession: applicationData.profession,
            income: applicationData.income
          },
          sharesSubscribed: applicationData.sharesSubscribed,
          membershipFee: applicationData.sharesSubscribed * 100
        }
      })

      if (error) throw error
      
      return true
    } catch (error) {
      console.error('Error sending application notification:', error)
      return false
    }
  }

  const requestDigitalSignature = async (membershipId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('cooperative_memberships')
        .update({
          digital_signature_requested_at: new Date().toISOString()
        })
        .eq('id', membershipId)

      if (error) throw error

      // Here we would normally send an email with signature link
      // For now, we'll just show a toast
      toast.info('E-Mail mit Unterschrifts-Aufforderung wird in Zukunft versendet')
      
      return true
    } catch (error) {
      console.error('Error requesting signature:', error)
      return false
    }
  }

  const completeDigitalSignature = async (membershipId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('cooperative_memberships')
        .update({
          digital_signature_completed_at: new Date().toISOString()
        })
        .eq('id', membershipId)

      if (error) throw error

      toast.success('Digitale Unterschrift erfolgreich geleistet!')
      await fetchMembership()
      
      return true
    } catch (error) {
      console.error('Error completing signature:', error)
      toast.error('Fehler bei der digitalen Unterschrift')
      return false
    }
  }

  // Fetch membership on mount and when user changes
  useEffect(() => {
    if (user?.id) {
      fetchMembership();
    } else {
      setMembership(null);
      setLoading(false);
    }
  }, [user?.id]); // Only depend on user.id to prevent loops

  const isCooperativeMember = membership?.status === 'approved'

  return {
    membership,
    loading,
    isCooperativeMember,
    fetchMembership,
    submitApplication,
    requestDigitalSignature,
    completeDigitalSignature
  }
}